import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { ExceptionService, UtilsService } from '../../xform-compat';
import { RmsBasicCrudService } from '../components/rms-basic-crud.service';
import { CONFIG } from '../../config';
import { MyAlertSummary } from '../../shared/models/my-alert-summary.interface';

/** Parameters for fetching a paginated list of alerts. */
export interface AlertListParams {
  page?: number;
  pagesize?: number;
  sort?: string;
  direction?: string;
  assignedtome?: boolean;
  createdbyme?: boolean;
  processed?: boolean;
  companyIds?: number;
  createdby?: number;
  assignedto?: number;
  q?: string;
}

/**
 * Unified data service for alert operations across all contexts (my-alerts and company-alerts).
 * Uses GET /api/rms/alarms as the primary listing endpoint with support for pagination,
 * filtering (assignedtome, createdbyme, processed, companyIds), and CRUD operations.
 */
@Injectable({ providedIn: 'root' })
export class AlertsDataService extends RmsBasicCrudService<any> {

  private readonly basePath = `${CONFIG.apiUrl}/rms`;

  constructor(
    http: HttpClient,
    utilsService: UtilsService,
    exceptionService: ExceptionService
  ) {
    super(http, utilsService, exceptionService);
  }

  /**
   * Fetches a paginated list of alerts with optional filters.
   */
  public getAlerts(params?: AlertListParams): Observable<any> {
    return this.getData(`${this.basePath}/alarms`, params);
  }

  /**
   * Fetches summary counts (active and archived totals) for alerts.
   * When companyId is provided, counts are scoped to that company.
   */
  public getAlertsSummary(companyId?: number): Observable<MyAlertSummary> {
    const params: any = {};
    if (companyId) {
      params.companyId = companyId;
    }
    return this.getHttp().get<MyAlertSummary>(`${this.basePath}/alarms/me/summary`, { params })
      .pipe(catchError(ex => this.getExceptions().catchBadResponse(ex)));
  }

  /**
   * Creates a new alert for a given company.
   */
  public createAlert(companyId: number, data: any): Observable<any> {
    return this.saveData(`${this.basePath}/companies/${companyId}/alarms`, data);
  }

  /**
   * Updates an existing alert for a given company.
   */
  public updateAlert(companyId: number, alertId: number, data: any): Observable<any> {
    return this.updateData(`${this.basePath}/companies/${companyId}/alarms/${alertId}`, data);
  }

  /**
   * Deletes an alert for a given company.
   */
  public deleteAlert(companyId: number, alertId: number): Observable<any> {
    return this.deleteData(`${this.basePath}/companies/${companyId}/alarms/${alertId}`);
  }

  /**
   * Assigns the current user to an alert.
   */
  public assignAlert(companyId: number, alarmId: number): Observable<any> {
    return this.saveData(`${this.basePath}/companies/${companyId}/alarms/${alarmId}/me`, {});
  }

  /**
   * Unassigns the current user from an alert.
   */
  public unassignAlert(companyId: number, alarmId: number): Observable<any> {
    return this.deleteData(`${this.basePath}/companies/${companyId}/alarms/${alarmId}/me`);
  }
}
